import {useEffect, useMemo, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {
  AlertCircle,
  ArrowLeft,
  Check,
  CheckCircle2,
  Clock3,
  Download,
  FileCheck2,
  History,
  Loader2,
  Minus,
  Package,
  Plus,
  RotateCcw,
  Save,
  Search,
  ShieldCheck,
  Trash2,
  X,
} from 'lucide-react'
import {ProductThumbnail} from '../shared/components/ProductThumbnail'
import {CardHelp} from '../shared/components/CardHelp'
import {
  fetchSavedPurchaseOrder,
  finalizePurchaseOrder,
  SavedPurchaseOrderAudit,
  SavedPurchaseOrderDetail,
  SavedPurchaseOrderItem,
  updatePreOrder,
} from '../services/purchaseOrdersApi'
import {exportFinalOrderXlsx} from './orderXlsx'
import {PurchaseOrderHistoryModal} from './PurchaseOrderHistoryModal'

interface EditableItem extends SavedPurchaseOrderItem {
  locallyRemoved: boolean
  editedQuantity: number
}

function formatCnpj(value: string) {
  const digits = value.replace(/\D/g, '')
  if (digits.length !== 14) return value
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`
}

function formatDate(value?: string | null) {
  if (!value) return '—'
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value))
}

function auditDescription(entry: SavedPurchaseOrderAudit) {
  const actor = entry.changed_by_name || 'Usuário'
  switch (entry.action) {
    case 'created':
      return `${actor} criou o pré-pedido a partir da sugestão automática.`
    case 'quantity_changed':
      return `${actor} alterou a quantidade de ${entry.product_name || entry.ean || 'um item'} de ${entry.old_value} para ${entry.new_value}.`
    case 'removed':
      return `${actor} removeu ${entry.product_name || entry.ean || 'um item'} do pré-pedido.`
    case 'restored':
      return `${actor} restaurou ${entry.product_name || entry.ean || 'um item'} no pré-pedido.`
    case 'finalized':
      return `${actor} criou o pedido final e bloqueou novas edições.`
    default:
      return `${actor} atualizou o pedido.`
  }
}

function priorityLabel(priority: SavedPurchaseOrderItem['priority_snapshot']) {
  if (priority === 'high') return {label: 'Alta', className: 'bg-red-50 text-red-700 ring-red-100'}
  if (priority === 'medium') return {label: 'Média', className: 'bg-amber-50 text-amber-700 ring-amber-100'}
  return {label: 'Baixa', className: 'bg-gray-100 text-gray-600 ring-gray-200'}
}

const ADJUSTMENT_REASON_OPTIONS = [
  {value: 'promotion', label: 'Promo\u00e7\u00e3o'},
  {value: 'commercial_agreement', label: 'Acordo Comercial'},
  {value: 'excess_inventory', label: 'Excesso de Estoque'},
  {value: 'manual_adjustment', label: 'Ajuste Manual'},
  {value: 'store_request', label: 'Pedido da Loja'},
  {value: 'minimum_order', label: 'Pedido M\u00ednimo do Fornecedor'},
  {value: 'package_multiple', label: 'M\u00faltiplo de Caixa ou Embalagem'},
  {value: 'product_availability', label: 'Disponibilidade do Produto'},
  {value: 'seasonality', label: 'Sazonalidade'},
  {value: 'other', label: 'Outros'},
] as const

type ReasonDialogAction = 'save' | 'finalize'
type AdjustmentReason = {code: string; text: string}


export function OrderDetailPage() {
  const {orderId: orderIdParam} = useParams()
  const navigate = useNavigate()
  const orderId = Number(orderIdParam)
  const [detail, setDetail] = useState<SavedPurchaseOrderDetail | null>(null)
  const [items, setItems] = useState<EditableItem[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [finalizing, setFinalizing] = useState(false)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [search, setSearch] = useState('')
  const [showFinalizeModal, setShowFinalizeModal] = useState(false)
  const [reasonDialogAction, setReasonDialogAction] = useState<ReasonDialogAction | null>(null)
  const [adjustmentReasonCode, setAdjustmentReasonCode] = useState('')
  const [adjustmentReasonText, setAdjustmentReasonText] = useState('')
  const [reasonError, setReasonError] = useState('')
  const [showHistoryModal, setShowHistoryModal] = useState(false)

  const syncDetail = (nextDetail: SavedPurchaseOrderDetail) => {
    setDetail(nextDetail)
    setItems(
      nextDetail.items.map((item) => ({
        ...item,
        locallyRemoved: item.is_removed,
        editedQuantity: item.final_quantity,
      }))
    )
  }

  const loadDetail = async () => {
    if (!Number.isInteger(orderId) || orderId <= 0) {
      setError('Pedido inválido.')
      setLoading(false)
      return
    }
    setLoading(true)
    setError('')
    try {
      syncDetail(await fetchSavedPurchaseOrder(orderId))
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Erro ao carregar pedido.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadDetail()
  }, [orderId])

  const dirty = useMemo(() => {
    if (!detail || detail.order.status !== 'draft') return false
    return items.some((item) =>
      item.locallyRemoved !== item.is_removed ||
      (!item.locallyRemoved && item.editedQuantity !== item.final_quantity)
    )
  }, [detail, items])

  useEffect(() => {
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirty) return
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warnBeforeUnload)
    return () => window.removeEventListener('beforeunload', warnBeforeUnload)
  }, [dirty])

  const activeItems = useMemo(() => items.filter((item) => !item.locallyRemoved), [items])
  const removedItems = useMemo(() => items.filter((item) => item.locallyRemoved), [items])
  const filteredItems = useMemo(() => {
    const query = search.toLowerCase().trim()
    if (!query) return activeItems
    return activeItems.filter((item) =>
      [item.product_name, item.ean, item.fornecedor || ''].join(' ').toLowerCase().includes(query)
    )
  }, [activeItems, search])

  const localTotals = useMemo(() => ({
    items: activeItems.length,
    units: activeItems.reduce((sum, item) => sum + item.editedQuantity, 0),
    changed: items.filter((item) =>
      item.locallyRemoved || item.editedQuantity !== item.suggested_quantity
    ).length,
  }), [activeItems, items])

  const updateQuantity = (itemId: number, quantity: number) => {
    const normalized = Math.max(1, Math.floor(Number(quantity) || 1))
    setItems((current) => current.map((item) =>
      item.item_id === itemId ? {...item, editedQuantity: normalized} : item
    ))
    setSuccessMessage('')
  }

  const setRemoved = (itemId: number, removed: boolean) => {
    setItems((current) => current.map((item) =>
      item.item_id === itemId ? {...item, locallyRemoved: removed} : item
    ))
    setSuccessMessage('')
  }

  const persistChanges = async (reason: AdjustmentReason): Promise<SavedPurchaseOrderDetail | null> => {
    if (!detail) return null
    setSaving(true)
    setError('')
    setSuccessMessage('')
    try {
      const updated = await updatePreOrder(detail.order.order_id, {
        version: detail.order.version,
        items: items
          .filter((item) => !item.locallyRemoved)
          .map((item) => ({item_id: item.item_id, quantity: item.editedQuantity})),
        adjustment_reason_code: reason.code,
        adjustment_reason_text: reason.text,
      })
      syncDetail(updated)
      setSuccessMessage('Alterações salvas com sucesso.')
      return updated
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Erro ao salvar alterações.')
      return null
    } finally {
      setSaving(false)
    }
  }

  const handleFinalize = async (reason?: AdjustmentReason) => {
    if (!detail) return
    setFinalizing(true)
    setError('')
    setSuccessMessage('')
    try {
      let currentDetail = detail
      if (dirty) {
        if (!reason) {
          setError('Informe o motivo das altera??es antes de criar o pedido.')
          return
        }
        const saved = await persistChanges(reason)
        if (!saved) return
        currentDetail = saved
      }
      const finalized = await finalizePurchaseOrder(
        currentDetail.order.order_id,
        currentDetail.order.version
      )
      syncDetail(finalized)
      setShowFinalizeModal(false)
      setReasonDialogAction(null)
      setSuccessMessage('Pedido criado. A edição foi bloqueada e o XLSX já pode ser gerado.')
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Erro ao criar pedido.')
    } finally {
      setFinalizing(false)
    }
  }

  const openReasonDialog = (action: ReasonDialogAction) => {
    setAdjustmentReasonCode('')
    setAdjustmentReasonText('')
    setReasonError('')
    setReasonDialogAction(action)
  }

  const confirmAdjustmentReason = async () => {
    if (!adjustmentReasonCode) {
      setReasonError('Selecione um motivo para continuar.')
      return
    }
    if (adjustmentReasonCode === 'other' && adjustmentReasonText.trim().length < 5) {
      setReasonError('Descreva o motivo em pelo menos 5 caracteres.')
      return
    }
    const reason = {code: adjustmentReasonCode, text: adjustmentReasonText.trim()}
    setReasonError('')
    if (reasonDialogAction === 'save') {
      const saved = await persistChanges(reason)
      if (saved) setReasonDialogAction(null)
      return
    }
    if (reasonDialogAction === 'finalize') {
      await handleFinalize(reason)
    }
  }

  if (loading) {
    return (
      <div className='flex min-h-[480px] items-center justify-center'>
        <div className='text-center text-gray-500'>
          <Loader2 className='mx-auto mb-3 h-9 w-9 animate-spin text-teal-600' />
          Carregando pré-pedido...
        </div>
      </div>
    )
  }

  if (!detail) {
    return (
      <div className='flex min-h-[480px] flex-col items-center justify-center text-center'>
        <span className='mb-4 rounded-full bg-red-50 p-4 text-red-600'><AlertCircle className='h-7 w-7' /></span>
        <h2 className='text-xl font-bold text-gray-900'>Não foi possível abrir o pedido</h2>
        <p className='mt-2 max-w-md text-sm text-gray-500'>{error}</p>
        <button onClick={() => navigate('/lojistas/pedidos')} className='mt-5 rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white'>Voltar para pedidos</button>
      </div>
    )
  }

  const {order, audit, adjustments} = detail
  const originLabel = order.generation_origin === 'seller'
    ? 'Modificado pelo seller'
    : order.generation_origin === 'lojista'
      ? 'Modificado pelo lojista'
      : 'Gerado integralmente pelo modelo'
  const isDraft = order.status === 'draft'

  return (
    <div className='space-y-6'>
      <button
        type='button'
        onClick={() => navigate('/lojistas/pedidos')}
        className='inline-flex items-center gap-2 text-sm font-semibold text-gray-500 transition hover:text-gray-900'
      >
        <ArrowLeft className='h-4 w-4' />
        Voltar para pedidos
      </button>

      <div className='rounded-2xl border border-gray-200 bg-white p-6 shadow-sm'>
        <div className='flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between'>
          <div>
            <div className='flex flex-wrap items-center gap-2.5'>
              <h1 className='text-2xl font-bold text-gray-900'>{order.order_number}</h1>
              {isDraft ? (
                <span className='inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 ring-1 ring-amber-100'><Clock3 className='h-3.5 w-3.5' />Pré-pedido editável</span>
              ) : (
                <span className='inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-100'><ShieldCheck className='h-3.5 w-3.5' />Pedido finalizado</span>
              )}
              <span className='rounded-full bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700 ring-1 ring-violet-100'>{originLabel}</span>
              {(order.is_modified || dirty) && (
                <span className='rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 ring-1 ring-blue-100'>Alterado</span>
              )}
            </div>
            <p className='mt-2 font-mono text-sm font-semibold text-gray-700'>{formatCnpj(order.cnpj)}</p>
            {order.razao_social && <p className='mt-1 text-sm text-gray-500'>{order.razao_social}</p>}
            <p className='mt-3 text-xs text-gray-400'>Criado automaticamente em {formatDate(order.created_at)} por {order.created_by_name || 'usuário da conta'}</p>
          </div>

          <div className='flex flex-wrap items-center gap-3'>
            <button
              type='button'
              onClick={() => setShowHistoryModal(true)}
              className='inline-flex items-center gap-2 rounded-lg border border-violet-200 bg-white px-4 py-2.5 text-sm font-semibold text-violet-700 shadow-sm transition hover:bg-violet-50'
            >
              <History className='h-4 w-4' />
              Histórico
            </button>
            {isDraft ? (
              <>
                <button
                  type='button'
                  onClick={() => openReasonDialog('save')}
                  disabled={!dirty || saving || finalizing}
                  className='inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40'
                >
                  {saving ? <Loader2 className='h-4 w-4 animate-spin' /> : <Save className='h-4 w-4' />}
                  Salvar alterações
                </button>
                <button
                  type='button'
                  onClick={() => dirty ? openReasonDialog('finalize') : setShowFinalizeModal(true)}
                  disabled={activeItems.length === 0 || saving || finalizing}
                  className='inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-40'
                >
                  <Check className='h-4 w-4' />
                  Criar pedido
                </button>
              </>
            ) : (
              <button
                type='button'
                onClick={() => exportFinalOrderXlsx(detail)}
                className='inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700'
              >
                <Download className='h-4 w-4' />
                Gerar Pedido (XLSX)
              </button>
            )}
          </div>
        </div>
      </div>

      {(error || successMessage) && (
        <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-sm ${error ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
          {error ? <AlertCircle className='mt-0.5 h-4 w-4 flex-none' /> : <CheckCircle2 className='mt-0.5 h-4 w-4 flex-none' />}
          <span>{error || successMessage}</span>
          <button type='button' onClick={() => { setError(''); setSuccessMessage('') }} className='ml-auto'><X className='h-4 w-4' /></button>
        </div>
      )}

      <div className='grid gap-4 md:grid-cols-3'>
        <div className='relative rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
          <CardHelp content='Itens que permanecem no pedido após as remoções realizadas pelo usuário.' className='absolute right-2 top-2' />
          <p className='pr-7 text-xs font-semibold uppercase tracking-wide text-gray-500'>Itens ativos</p>
          <p className='mt-2 text-3xl font-bold text-gray-900'>{localTotals.items}</p>
          <p className='mt-1 text-xs text-gray-400'>{items.length} na sugestão original</p>
        </div>
        <div className='relative rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
          <CardHelp content='Soma das quantidades finais dos itens ativos do pedido.' className='absolute right-2 top-2' />
          <p className='pr-7 text-xs font-semibold uppercase tracking-wide text-gray-500'>Total de unidades</p>
          <p className='mt-2 text-3xl font-bold text-gray-900'>{localTotals.units}</p>
          <p className='mt-1 text-xs text-gray-400'>{order.original_total_units} sugeridas originalmente</p>
        </div>
        <div className='relative rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
          <CardHelp content='Itens cuja quantidade foi modificada ou que foram removidos em relação à sugestão original.' className='absolute right-2 top-2' />
          <p className='pr-7 text-xs font-semibold uppercase tracking-wide text-gray-500'>Itens alterados</p>
          <p className='mt-2 text-3xl font-bold text-gray-900'>{localTotals.changed}</p>
          <p className='mt-1 text-xs text-gray-400'>{dirty ? 'existem alterações não salvas' : 'todas as alterações estão salvas'}</p>
        </div>
      </div>

      <div className='overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm'>
        <div className='flex flex-col gap-4 border-b border-gray-100 p-5 md:flex-row md:items-center md:justify-between'>
          <div>
            <h2 className='font-bold text-gray-900'>Itens do {isDraft ? 'pré-pedido' : 'pedido'}</h2>
            <p className='mt-1 text-sm text-gray-500'>{isDraft ? 'Ajuste as quantidades ou remova itens antes de criar o pedido.' : 'Este é o snapshot final e não pode mais ser editado.'}</p>
          </div>
          <div className='relative w-full md:max-w-sm'>
            <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder='Buscar produto, EAN ou fornecedor...'
              className='w-full rounded-lg border border-gray-200 py-2.5 pl-10 pr-3 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100'
            />
          </div>
        </div>

        <div className='overflow-x-auto'>
          <table className='w-full min-w-[980px]'>
            <thead className='bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500'>
              <tr>
                <th className='px-6 py-3.5'>Produto</th>
                <th className='px-4 py-3.5'>Fornecedor</th>
                <th className='px-4 py-3.5'>Prioridade</th>
                <th className='px-4 py-3.5 text-right'>Sugerido</th>
                <th className='px-4 py-3.5 text-center'>{isDraft ? 'Quantidade final' : 'Quantidade'}</th>
                {isDraft && <th className='px-6 py-3.5 text-right'>Ação</th>}
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-100'>
              {filteredItems.map((item) => {
                const priority = priorityLabel(item.priority_snapshot)
                const changed = item.editedQuantity !== item.suggested_quantity
                return (
                  <tr key={item.item_id} className='transition hover:bg-gray-50/70'>
                    <td className='px-6 py-4'>
                      <div className='flex items-center gap-3'>
                        <ProductThumbnail src={item.foto || undefined} alt={item.product_name} ean={item.ean} details={{
                          estoqueAtual: item.stock_snapshot, cmd: item.cmd_snapshot,
                          cobertura: item.coverage_snapshot, leadTime: item.lead_time_snapshot,
                          estoqueAlvo: item.target_stock_snapshot, pedidoSugerido: item.suggested_quantity,
                          fornecedor: item.fornecedor || undefined,
                        }} />
                        <div className='min-w-0'>
                          <p className='max-w-[360px] truncate text-sm font-semibold text-gray-900'>{item.product_name}</p>
                          <p className='mt-0.5 text-xs text-gray-500'>EAN: {item.ean}</p>
                        </div>
                      </div>
                    </td>
                    <td className='px-4 py-4 text-sm text-gray-600'>{item.fornecedor || 'Não informado'}</td>
                    <td className='px-4 py-4'><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${priority.className}`}>{priority.label}</span></td>
                    <td className='px-4 py-4 text-right text-sm font-semibold text-gray-500'>{item.suggested_quantity}</td>
                    <td className='px-4 py-4'>
                      {isDraft ? (
                        <div className='mx-auto flex w-fit items-center rounded-lg border border-gray-200 bg-white shadow-sm'>
                          <button type='button' onClick={() => updateQuantity(item.item_id, item.editedQuantity - 1)} className='p-2 text-gray-500 transition hover:bg-gray-50 hover:text-gray-900'><Minus className='h-4 w-4' /></button>
                          <input
                            type='number'
                            min={1}
                            value={item.editedQuantity}
                            onChange={(event) => updateQuantity(item.item_id, Number(event.target.value))}
                            className={`w-16 border-x border-gray-200 py-2 text-center text-sm font-bold outline-none ${changed ? 'bg-blue-50 text-blue-700' : 'text-gray-900'}`}
                          />
                          <button type='button' onClick={() => updateQuantity(item.item_id, item.editedQuantity + 1)} className='p-2 text-gray-500 transition hover:bg-gray-50 hover:text-gray-900'><Plus className='h-4 w-4' /></button>
                        </div>
                      ) : (
                        <p className={`text-center text-sm font-bold ${changed ? 'text-blue-700' : 'text-gray-900'}`}>{item.editedQuantity} un</p>
                      )}
                    </td>
                    {isDraft && (
                      <td className='px-6 py-4 text-right'>
                        <button type='button' onClick={() => setRemoved(item.item_id, true)} className='inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50'><Trash2 className='h-4 w-4' />Remover</button>
                      </td>
                    )}
                  </tr>
                )
              })}
              {filteredItems.length === 0 && (
                <tr><td colSpan={isDraft ? 6 : 5} className='px-6 py-14 text-center text-sm text-gray-500'><Package className='mx-auto mb-3 h-8 w-8 text-gray-300' />Nenhum item encontrado.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {isDraft && removedItems.length > 0 && (
          <div className='border-t border-red-100 bg-red-50/50 p-5'>
            <div className='mb-3 flex items-center gap-2 text-sm font-semibold text-red-800'><Trash2 className='h-4 w-4' />{removedItems.length} item(ns) removido(s)</div>
            <div className='flex flex-wrap gap-2'>
              {removedItems.map((item) => (
                <button key={item.item_id} type='button' onClick={() => setRemoved(item.item_id, false)} className='inline-flex max-w-full items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm transition hover:border-red-300'>
                  <RotateCcw className='h-4 w-4 flex-none text-red-600' />
                  <span className='max-w-[260px] truncate'>{item.product_name}</span>
                  <span className='text-xs font-semibold text-red-600'>Restaurar</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className='hidden'>
        <div className='mb-5 flex items-center gap-3'>
          <span className='rounded-xl bg-violet-50 p-2.5 text-violet-600'><History className='h-5 w-5' /></span>
          <div><h2 className='font-bold text-gray-900'>Histórico e auditoria</h2><p className='text-sm text-gray-500'>Registro permanente das decisões tomadas neste pedido.</p></div>
        </div>
        {adjustments.length > 0 && (
          <div className='mb-6 grid gap-3 md:grid-cols-2'>
            {adjustments.map((adjustment) => (
              <div key={adjustment.adjustment_id} className='rounded-xl border border-blue-100 bg-blue-50/60 p-4'>
                <div className='flex flex-wrap items-center justify-between gap-2'>
                  <span className='text-sm font-bold text-blue-900'>{adjustment.reason_label}</span>
                  <span className='rounded-full bg-white px-2 py-1 text-xs font-semibold text-blue-700'>{adjustment.actor_type === 'seller' ? 'Seller' : 'Lojista'}</span>
                </div>
                {adjustment.reason_text && <p className='mt-2 text-sm leading-5 text-gray-700'>{adjustment.reason_text}</p>}
                <p className='mt-3 text-xs text-gray-500'>{adjustment.changed_by_name || 'Usu?rio'} {'?'} {formatDate(adjustment.created_at)}</p>
              </div>
            ))}
          </div>
        )}
        <div className='space-y-0'>
          {audit.map((entry, index) => (
            <div key={entry.audit_id} className='relative flex gap-4 pb-5 last:pb-0'>
              {index < audit.length - 1 && <span className='absolute left-[15px] top-8 h-[calc(100%-20px)] w-px bg-gray-200' />}
              <span className={`relative z-10 mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-full ${entry.action === 'finalized' ? 'bg-emerald-100 text-emerald-700' : entry.action === 'created' ? 'bg-teal-100 text-teal-700' : 'bg-blue-100 text-blue-700'}`}>
                {entry.action === 'finalized' ? <FileCheck2 className='h-4 w-4' /> : entry.action === 'created' ? <CheckCircle2 className='h-4 w-4' /> : <History className='h-4 w-4' />}
              </span>
              <div><p className='text-sm text-gray-700'>{auditDescription(entry)}</p><p className='mt-1 text-xs text-gray-400'>{formatDate(entry.changed_at)}</p></div>
            </div>
          ))}
        </div>
      </div>

      <PurchaseOrderHistoryModal
        open={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        title={`Histórico de ${order.order_number}`}
        subtitle='Registro permanente das decisões tomadas neste pedido.'
        groups={[{
          order_id: order.order_id,
          order_number: order.order_number,
          cnpj: order.cnpj,
          razao_social: order.razao_social,
          status: order.status,
          generation_origin: order.generation_origin,
          is_modified: order.is_modified,
          created_at: order.created_at,
          updated_at: order.updated_at,
          changes: audit,
        }]}
      />

      {reasonDialogAction && (
        <div className='fixed inset-0 z-[12000] flex items-center justify-center bg-slate-950/55 p-4'>
          <div className='w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl'>
            <div className='flex items-start justify-between gap-4'>
              <div>
                <h3 className='text-lg font-bold text-gray-900'>{'Justifique a altera\u00e7\u00e3o do pedido'}</h3>
                <p className='mt-2 text-sm leading-6 text-gray-500'>{'Essa informa\u00e7\u00e3o ser\u00e1 ligada \u00e0s mudan\u00e7as realizadas e usada para melhorar a automa\u00e7\u00e3o do modelo.'}</p>
              </div>
              <button type='button' onClick={() => setReasonDialogAction(null)} disabled={saving || finalizing} className='rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700'><X className='h-5 w-5' /></button>
            </div>
            <div className='mt-5 grid gap-2 sm:grid-cols-2'>
              {ADJUSTMENT_REASON_OPTIONS.map((option) => (
                <button key={option.value} type='button' onClick={() => {setAdjustmentReasonCode(option.value); setReasonError('')}} className={`rounded-xl border px-4 py-3 text-left text-sm font-medium transition ${adjustmentReasonCode === option.value ? 'border-blue-500 bg-blue-50 text-blue-800 ring-2 ring-blue-100' : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'}`}>
                  {option.label}
                </button>
              ))}
            </div>
            {adjustmentReasonCode === 'other' && (
              <div className='mt-4'>
                <label htmlFor='adjustment-reason-text' className='text-sm font-semibold text-gray-700'>{'Descreva o motivo'}</label>
                <textarea id='adjustment-reason-text' value={adjustmentReasonText} maxLength={500} onChange={(event) => setAdjustmentReasonText(event.target.value)} rows={4} placeholder='Conte o que motivou esse ajuste...' className='mt-2 w-full resize-none rounded-xl border border-gray-300 p-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100' />
                <p className='mt-1 text-right text-xs text-gray-400'>{adjustmentReasonText.length}/500</p>
              </div>
            )}
            {reasonError && <div className='mt-4 flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700'><AlertCircle className='h-4 w-4' />{reasonError}</div>}
            <div className='mt-6 flex justify-end gap-3'>
              <button type='button' onClick={() => setReasonDialogAction(null)} disabled={saving || finalizing} className='rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50'>Cancelar</button>
              <button type='button' onClick={confirmAdjustmentReason} disabled={saving || finalizing || !adjustmentReasonCode} className='inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50'>
                {(saving || finalizing) ? <Loader2 className='h-4 w-4 animate-spin' /> : <Check className='h-4 w-4' />}
                {reasonDialogAction === 'finalize' ? 'Salvar e criar pedido' : 'Salvar altera\u00e7\u00f5es'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showFinalizeModal && (
        <div className='fixed inset-0 z-[11000] flex items-center justify-center bg-slate-950/50 p-4'>
          <div className='w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl'>
            <div className='flex items-start gap-4'>
              <span className='rounded-full bg-teal-50 p-3 text-teal-700'><ShieldCheck className='h-6 w-6' /></span>
              <div><h3 className='text-lg font-bold text-gray-900'>Criar pedido final?</h3><p className='mt-2 text-sm leading-6 text-gray-500'>Após esta confirmação, o pedido ficará bloqueado para edição. As quantidades atuais serão consideradas finais e o botão de gerar XLSX será liberado.</p></div>
            </div>
            <div className='mt-5 rounded-xl bg-gray-50 p-4 text-sm'>
              <div className='flex justify-between'><span className='text-gray-500'>Itens</span><strong className='text-gray-900'>{localTotals.items}</strong></div>
              <div className='mt-2 flex justify-between'><span className='text-gray-500'>Unidades</span><strong className='text-gray-900'>{localTotals.units}</strong></div>
              {dirty && <div className='mt-3 border-t border-gray-200 pt-3 text-xs font-medium text-blue-700'>As alterações pendentes serão salvas antes da finalização.</div>}
            </div>
            <div className='mt-6 flex justify-end gap-3'>
              <button type='button' onClick={() => setShowFinalizeModal(false)} disabled={finalizing} className='rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50'>Continuar revisando</button>
              <button type='button' onClick={() => handleFinalize()} disabled={finalizing} className='inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-50'>
                {finalizing ? <Loader2 className='h-4 w-4 animate-spin' /> : <Check className='h-4 w-4' />}
                Criar pedido
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
